import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CreditCardIcon,
  EyeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Input,
  Select,
  Card,
  CardBody,
  Badge,
  EmptyState,
  Spinner,
} from '@/components/ui';
import { transactionApi, getErrorMessage } from '@/lib/api';
import type { Transaction, TransactionStatus, PaymentMethod } from '@/types';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'AUTHORIZED', label: 'Authorized' },
  { value: 'CAPTURED', label: 'Captured' },
  { value: 'SETTLED', label: 'Settled' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'PARTIALLY_REFUNDED', label: 'Partially Refunded' },
  { value: 'CHARGEBACK', label: 'Chargeback' },
];

const paymentMethodOptions = [
  { value: '', label: 'All Methods' },
  { value: 'CARD', label: 'Card' },
  { value: 'GCASH', label: 'GCash' },
  { value: 'MAYA', label: 'Maya' },
  { value: 'GRAB_PAY', label: 'GrabPay' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'OTHER', label: 'Other' },
];

interface TransactionWithLink extends Omit<Transaction, 'paymentLink'> {
  paymentLink: {
    id: string;
    linkCode: string;
    title: string;
    customerName?: string;
    customerEmail?: string;
  };
}

export function TransactionsPage() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', { search, status, paymentMethod, page }],
    queryFn: async () => {
      const response = await transactionApi.list({
        search: search || undefined,
        status: status || undefined,
        paymentMethod: paymentMethod || undefined,
        page,
        limit: 20,
      });
      return response.data;
    },
  });

  const { data: statsData } = useQuery({
    queryKey: ['transaction-statistics'],
    queryFn: async () => {
      const response = await transactionApi.getStatistics();
      return response.data.data;
    },
  });

  const handleExport = async () => {
    try {
      const response = await transactionApi.exportCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Transactions exported successfully');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const formatCurrency = (amount: number | string, currency = 'PHP') => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(numAmount);
  };

  const getStatusBadge = (txStatus: TransactionStatus) => {
    const config: Record<TransactionStatus, { variant: 'success' | 'warning' | 'error' | 'info' | 'gray'; label: string }> = {
      AUTHORIZED: { variant: 'success', label: 'Authorized' },
      CAPTURED: { variant: 'success', label: 'Captured' },
      SETTLED: { variant: 'success', label: 'Settled' },
      PENDING: { variant: 'warning', label: 'Pending' },
      FAILED: { variant: 'error', label: 'Failed' },
      CANCELLED: { variant: 'gray', label: 'Cancelled' },
      REFUNDED: { variant: 'info', label: 'Refunded' },
      PARTIALLY_REFUNDED: { variant: 'warning', label: 'Partial Refund' },
      CHARGEBACK: { variant: 'error', label: 'Chargeback' },
    };
    const { variant, label } = config[txStatus] || { variant: 'gray', label: txStatus };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPaymentMethodLabel = (method: PaymentMethod | null | undefined) => {
    if (!method) return '-';
    const labels: Record<PaymentMethod, string> = {
      CARD: 'Card',
      GCASH: 'GCash',
      MAYA: 'Maya',
      GRAB_PAY: 'GrabPay',
      BANK_TRANSFER: 'Bank Transfer',
      OTHER: 'Other',
    };
    return labels[method] || method;
  };

  const transactions = data?.data as TransactionWithLink[] | undefined;
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">View and manage all payment transactions</p>
        </div>
        <Button
          variant="outline"
          leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </div>

      {/* Statistics cards */}
      {statsData?.overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardBody className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-50 rounded-lg">
                  <CreditCardIcon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Transactions</p>
                  <p className="text-xl font-bold text-gray-900">
                    {statsData.overview.totalTransactions.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success-50 rounded-lg">
                  <CheckCircleIcon className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Successful</p>
                  <p className="text-xl font-bold text-gray-900">
                    {statsData.overview.successfulTransactions.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success-50 rounded-lg">
                  <span className="text-success font-bold">₱</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(statsData.overview.totalAmount)}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-info-50 rounded-lg">
                  <span className="text-info font-bold">%</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Success Rate</p>
                  <p className="text-xl font-bold text-gray-900">
                    {statsData.overview.successRate.toFixed(1)}%
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Search and filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by reference, email, or card number..."
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Button
              variant="outline"
              leftIcon={<FunnelIcon className="w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <Select
                options={statusOptions}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                placeholder="Filter by status"
              />
              <Select
                options={paymentMethodOptions}
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setPage(1);
                }}
                placeholder="Filter by payment method"
              />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Transactions list */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-error">{getErrorMessage(error)}</p>
            </div>
          ) : !transactions || transactions.length === 0 ? (
            <EmptyState
              icon={<CreditCardIcon className="w-12 h-12" />}
              title="No transactions found"
              description="Transactions will appear here once payments are processed"
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Transaction</th>
                      <th>Payment Link</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>
                          <div className="min-w-[150px]">
                            <p className="font-mono text-sm text-gray-900">
                              {tx.transactionRef}
                            </p>
                            {tx.pspReference && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                PSP: {tx.pspReference}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="min-w-[150px]">
                            <p className="text-sm text-gray-900">
                              {tx.paymentLink?.title || '-'}
                            </p>
                            <p className="text-xs text-gray-500 font-mono">
                              {tx.paymentLink?.linkCode || '-'}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div className="min-w-[150px]">
                            <p className="text-sm text-gray-900">
                              {tx.payerName || tx.paymentLink?.customerName || '-'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {tx.payerEmail || tx.paymentLink?.customerEmail || '-'}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div className="min-w-[100px]">
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(tx.amount, tx.currency)}
                            </p>
                            {tx.refundedAmount > 0 && (
                              <p className="text-xs text-error">
                                Refunded: {formatCurrency(tx.refundedAmount, tx.currency)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="min-w-[100px]">
                            <p className="text-sm text-gray-900">
                              {getPaymentMethodLabel(tx.paymentMethod)}
                            </p>
                            {tx.cardLastFour && (
                              <p className="text-xs text-gray-500">
                                {tx.cardBrand} •••• {tx.cardLastFour}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(tx.status)}</td>
                        <td className="text-sm text-gray-500">
                          {format(new Date(tx.createdAt), 'MMM d, yyyy')}
                          <br />
                          <span className="text-xs">
                            {format(new Date(tx.createdAt), 'h:mm a')}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin/transactions/${tx.id}`)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                              title="View details"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} transactions
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

    </div>
  );
}
